import React, { useState, useEffect } from "react";
import { GridLineSettings, DEFAULT_GRID } from "@/components/GridLineSettings";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Palette, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { key: "navy", label: "Classic Navy", accent: "#1A2A5E", secondary: "#F4571E" },
  { key: "green", label: "Emerald", accent: "#0F5132", secondary: "#16A34A" },
  { key: "orange", label: "Sunset", accent: "#9A3412", secondary: "#F97316" },
  { key: "purple", label: "Royal Purple", accent: "#4C1D95", secondary: "#7C3AED" },
  { key: "teal", label: "Teal", accent: "#134E4A", secondary: "#0D9488" },
  { key: "charcoal", label: "Charcoal", accent: "#111827", secondary: "#4B5563" },
];
const FONTS = ["Arial", "Georgia", "Times New Roman", "Trebuchet MS", "Courier New", "Verdana"];

export default function InvoiceTheme() {
  const [bill, setBill] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const { data } = await api.get("/settings/bill"); setBill(data || {}); }
    catch (e) { toast.error(apiError(e)); }
  }
  useEffect(() => { load(); }, []);

  const accent = bill?.theme_accent || "#1A2A5E";
  const secondary = bill?.theme_secondary || "#F4571E";
  const font = bill?.font_family || "Arial";

  function set(k, v) { setBill((b) => ({ ...b, [k]: v })); }
  function applyPreset(p) { setBill((b) => ({ ...b, theme_accent: p.accent, theme_secondary: p.secondary, theme_key: p.key })); }

  async function save() {
    setSaving(true);
    try { await api.put("/settings/bill", bill); toast.success("Invoice theme saved · applies to prints"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }

  if (!bill) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="invoice-theme-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#0F172A] flex items-center gap-2"><Palette className="h-6 w-6 text-[#1A2A5E]" /> Invoice Theme</h2>
        <p className="text-slate-500 text-sm">Customize colors and fonts for your printed invoices</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-5">
        <Card className="p-5 border-slate-200 space-y-5">
          <div>
            <Label className="mb-2 block">Color Theme</Label>
            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => applyPreset(p)} data-testid={`theme-${p.key}`}
                  className={`relative rounded-xl p-3 border-2 transition-all ${bill.theme_accent === p.accent ? "border-[#F4571E] shadow-md" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex gap-1 mb-2"><span className="h-6 w-6 rounded" style={{ background: p.accent }} /><span className="h-6 w-6 rounded" style={{ background: p.secondary }} /></div>
                  <div className="text-xs font-medium text-slate-600 text-left">{p.label}</div>
                  {bill.theme_accent === p.accent && <Check className="h-4 w-4 text-[#F4571E] absolute top-2 right-2" />}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Accent (headers)</Label><input type="color" value={accent} onChange={(e) => set("theme_accent", e.target.value)} className="mt-1 w-full h-10 rounded border cursor-pointer" data-testid="theme-accent-picker" /></div>
            <div><Label>Secondary</Label><input type="color" value={secondary} onChange={(e) => set("theme_secondary", e.target.value)} className="mt-1 w-full h-10 rounded border cursor-pointer" data-testid="theme-secondary-picker" /></div>
          </div>
          <div>
            <Label>Font Family</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {FONTS.map((f) => <button key={f} onClick={() => set("font_family", f)} data-testid={`font-${f.replace(/\s/g, "-")}`} className={`text-sm py-2 rounded border ${font === f ? "border-[#F4571E] bg-[#F4571E]/5 text-[#1A2A5E]" : "border-slate-200 text-slate-600"}`} style={{ fontFamily: f }}>{f}</button>)}
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#1A2A5E]" data-testid="theme-save">{saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Theme"}</Button>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-3">Live Preview</div>
          <div className="border rounded-lg overflow-hidden" style={{ fontFamily: font }} data-testid="theme-preview">
            <div className="p-4 border-b-4" style={{ borderColor: accent }}>
              <div className="text-xl font-extrabold" style={{ color: accent }}>{bill.business_name || "Your Business"}</div>
              <div className="text-[11px] text-slate-500">GSTIN · Address line · Phone</div>
            </div>
            <div className="text-center text-lg font-extrabold tracking-widest py-2" style={{ color: secondary }}>TAX INVOICE</div>
            <table className="w-full text-xs">
              <thead><tr style={{ background: accent, color: "#fff" }}><th className="text-left p-2">Item</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Amount</th></tr></thead>
              <tbody>
                <tr className="border-b"><td className="p-2">Sample Product A</td><td className="text-right p-2">2</td><td className="text-right p-2">₹200.00</td></tr>
                <tr className="border-b"><td className="p-2">Sample Product B</td><td className="text-right p-2">1</td><td className="text-right p-2">₹150.00</td></tr>
              </tbody>
            </table>
            <div className="p-3 text-right font-extrabold" style={{ color: accent }}>Grand Total: ₹350.00</div>
          </div>
        </Card>
      </div>
      <GridLineSettings bill={bill} setBill={setBill} />
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-[#1A2A5E]" data-testid="grid-save">{saving ? "Saving..." : "Save Theme"}</Button>
        <Button variant="outline" onClick={() => setBill({ ...bill, grid: { ...DEFAULT_GRID } })} data-testid="grid-reset-default">Reset to Default</Button>
      </div>
    </div>
  );
}
